// RelationCreateDialog.tsx — Refined-Modal zum Anlegen einer Verknüpfung in der
// Kontakt-Detailansicht. Ein Dialog für alle Tabellen (kind):
//   gfg         — Firma: Geschäftsführer/Gesellschafter (+ Anteile)
//   verant      — Firma: Verantwortlichkeit (VERANT_FUNCTIONS) + intern/extern
//   firmaConn   — Firma: Verbindung zu Firma (Tochterfirma/Mutterfirma/Beteiligung)
//   personFirma — Person: Verbindung zu Firma (GF/Gesellschafter/Verantwortlichkeit)
//   household   — Person: Haushaltsmitglied (Rolle aus HOUSEHOLD_ROLES, Label nach Geschlecht abgeleitet)
//   weitere     — Person: weitere Beziehung (gleiche Rollen-Logik, kein Haushalt)
// Kontaktwahl via RelationContactPicker (für Haushalt/Weitere nur Personen);
// Speichern via POST /contact-relation.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { RelationContactPicker } from "./RelationContactPicker";
import { apiUrl } from "../../utils/api";
import { VERANT_FUNCTIONS } from "../risikoanalyseStep/schema";
import { genderOf, RELATIONSHIP_CATALOG, RELATIONSHIP_FREE, isCatalogLabel, catalogReverseLabel, reverseOptions } from "../../utils/householdRole";
import { canonicalFirmaConn } from "../relationsInline/types";
import { haushaltsanschriftAbgleichen } from "../relationsInline/persistRelations";
import { hinweisen } from "./bestaetigen";
import {
  BETEILIGUNG,
  FIRMA_CONN_ROLES,
  FIRMA_CONN_ROLE_VALUES,
  FIRMA_SYMMETRIC,
  firmaConnRolleAusRichtung,
  GESELLSCHAFTERIN,
  JOINT_VENTURE,
  MUTTERFIRMA,
  SCHWESTER,
  TOCHTERFIRMA,
} from "../../data/firmenverbindungen";
import { ANTEIL_MAX, formatiereAnteil } from "../../utils/anteilFormat";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

export type RelKind = "gfg" | "verant" | "firmaConn" | "personFirma" | "household" | "weitere";

const TITLES: Record<RelKind, string> = {
  gfg: "Geschäftsführer / Gesellschafter hinzufügen",
  verant: "Verantwortlichkeit hinzufügen",
  firmaConn: "Verbindung zu Unternehmen hinzufügen",
  personFirma: "Verbindung zu Unternehmen hinzufügen",
  household: "Haushaltsmitglied hinzufügen",
  weitere: "Beziehung hinzufügen",
};

// Titel im Edit-Modus (Gegenstück zu TITLES).
const EDIT_TITLES: Record<RelKind, string> = {
  gfg: "Geschäftsführer / Gesellschafter bearbeiten",
  verant: "Verantwortlichkeit bearbeiten",
  firmaConn: "Verbindung zu Unternehmen bearbeiten",
  personFirma: "Verbindung zu Unternehmen bearbeiten",
  household: "Haushaltsmitglied bearbeiten",
  weitere: "Beziehung bearbeiten",
};

const GFG_ROLES = ["Geschäftsführer", "Gesellschafter", "Geschäftsführer und Gesellschafter"];
const PERSON_FIRMA_ROLES = ["Geschäftsführer", "Gesellschafter", "Geschäftsführer und Gesellschafter", "Verantwortlichkeit"];

// ─── Fehlerberichterstattung beim Speichern ──────────────────────────────────
// Beide Speicher-Pfade (save + saveEdit) meldeten früher pauschal „Speichern
// fehlgeschlagen." aus einem bindungslosen `catch {`. Damit war ein 403 des
// AuthzGuards von einem abgebrochenen Request nicht zu unterscheiden, der
// Statuscode existierte für die Dauer eines Microtasks und war dann weg — die
// Diagnose eines real gemeldeten Falls kostete deshalb eine komplette Runde
// Log-Archäologie. Ab hier gilt: Status + Backend-Meldung gehen in den
// Fehlertext, der Originalfehler zusätzlich in die Browser-Konsole.

type SaveErrorKind = "http" | "network";

/** Fehler eines Speicher-Requests — trägt die Unterscheidung „Server hat
 *  geantwortet" (http, mit Status/Meldung) vs. „Request kam nie an" (network). */
class SaveError extends Error {
  readonly kind: SaveErrorKind;
  readonly status?: number;
  readonly detail?: string;
  /** Der ursprünglich geworfene Fehler (nur bei kind==="network"). */
  readonly origin?: unknown;
  constructor(kind: SaveErrorKind, status?: number, detail?: string, origin?: unknown) {
    super(kind === "http" ? `HTTP ${status}${detail ? `: ${detail}` : ""}` : "Netzwerkfehler");
    this.name = "SaveError";
    this.kind = kind;
    this.status = status;
    this.detail = detail;
    this.origin = origin;
  }
}

/**
 * Fehlermeldung aus einer nicht-ok-Antwort lesen. NestJS liefert
 * `{ statusCode, message, error }`, wobei `message` String ODER String-Array ist
 * (ValidationPipe). Kein JSON (z. B. HTML-Fehlerseite eines Proxys) → gekürzter
 * Rohtext. Schlägt schon das Lesen fehl, bleibt wenigstens der Status.
 */
const clip = (s: string): string | undefined => {
  const t = s.trim();
  return t ? (t.length > 160 ? `${t.slice(0, 160)}…` : t) : undefined;
};

async function readErrorDetail(res: Response): Promise<string | undefined> {
  try {
    const raw = (await res.text()).trim();
    if (!raw) return undefined;
    try {
      const j = JSON.parse(raw);
      const m = j?.message ?? j?.error;
      // Länge in JEDEM Zweig begrenzen: ein ValidationPipe-Array kann beliebig
      // viele Einzelmeldungen enthalten und würde den Dialog sprengen.
      if (Array.isArray(m)) return clip(m.filter(Boolean).join(", "));
      if (typeof m === "string" && m.trim()) return clip(m);
      return undefined;
    } catch {
      return clip(raw);
    }
  } catch {
    return undefined;
  }
}

/**
 * Einen Speicher-Request absetzen und bei Misserfolg einen SaveError werfen.
 * Der `try` liegt AUSSCHLIESSLICH um `fetch` — nur so ist ein echter
 * Verbindungsfehler sauber von einem HTTP-Fehlerstatus zu trennen (ein
 * `instanceof TypeError` im äußeren catch würde auch Programmierfehler
 * fälschlich als „Server nicht erreichbar" ausgeben).
 */
async function sendSave(url: string, init: RequestInit): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (origin) {
    throw new SaveError("network", undefined, undefined, origin);
  }
  if (res.ok) return;
  throw new SaveError("http", res.status, await readErrorDetail(res));
}

// Deutscher Klartext für die Status, bei denen die Backend-Meldung für einen
// Sachbearbeiter nichts hergibt. Der AuthzGuard antwortet auf 401/403 immer mit
// einem englischen Entwicklertext („Missing required scope"); den bekäme der
// Nutzer sonst wörtlich zu lesen. Für diese Status gewinnt deshalb der Hinweis,
// die Backend-Meldung steht weiterhin in der Konsole.
const STATUS_HINT: Record<number, string> = {
  401: "Anmeldung abgelaufen — bitte neu anmelden.",
  403: "Keine Berechtigung für diese Änderung.",
  404: "Der Datensatz existiert nicht (mehr).",
};

/**
 * Einheitlicher, deutscher Fehlertext für beide Speicher-Pfade — plus
 * `console.error` mit dem Originalfehler, damit die Browser-Konsole bei einer
 * künftigen Meldung überhaupt etwas hergibt.
 */
function saveErrorText(err: unknown): string {
  console.error("[RelationCreateDialog] Speichern fehlgeschlagen:", err);
  if (err instanceof SaveError) {
    if (err.kind === "network") {
      return "Server nicht erreichbar — bitte Verbindung prüfen und erneut versuchen.";
    }
    const detail = STATUS_HINT[err.status ?? 0] || err.detail;
    return `Speichern fehlgeschlagen (${err.status})${detail ? `: ${detail}` : "."}`;
  }
  // Alles andere (unerwarteter Throw im Speicher-Ablauf): wenigstens die
  // Original-Meldung zeigen, statt sie zu verschlucken.
  const msg = (err instanceof Error ? err.message : String(err ?? "")).trim();
  return msg ? `Speichern fehlgeschlagen: ${msg}` : "Speichern fehlgeschlagen.";
}

/**
 * Beschreibung der bestehenden Zeile für den Edit-Modus. `relType` ist die im
 * ANGEZEIGTEN Sinn (aktuelle Kontaktsicht) — bei firmaConn ggf. „Mutterfirma",
 * da die eingehende Tochterfirma-Zeile invertiert dargestellt wird. `srcId`/`destId`
 * sind die echten ContactSrc/ContactDest-Ids der gespeicherten Zeile (Richtung).
 */
export type EditRelation = {
  relationId: number;
  srcId: number;
  destId: number;
  other: { id: number; name: string; type: string; gender?: string | null };
  relType: string;
  /** Roh-Label der Zeile (relationType = „was dest für src ist"), für Freitext-Prefill. */
  rawRelType?: string;
  /** Gespeicherte Freitext-Gegenrichtung (reverseRelationType), für Prefill. */
  reverseRelType?: string | null;
  function?: string;
  share?: string;
  isExtern: boolean;
  sameHousehold: boolean;
};

// Rückabbildung einer Person↔Firma-Zeile (relationType + function) auf die
// Rollen-Auswahl (PERSON_FIRMA_ROLES).
function personFirmaRoleFromEdit(e: EditRelation): string {
  if (e.relType === "Mensch fürs Unternehmen" || e.relType === "Verantwortlichkeit") return "Verantwortlichkeit";
  if (e.relType === "Gesellschafter" && e.function === "geschäftsführend") return "Geschäftsführer und Gesellschafter";
  if (e.relType === "Gesellschafter") return "Gesellschafter";
  if (e.relType === "Geschäftsführer") return "Geschäftsführer";
  return PERSON_FIRMA_ROLES[0];
}

/**
 * Prefill der Beziehungs-Felder aus einer bestehenden Zeile.
 * Vorwärts = „was other für self ist" (angezeigtes `relType`).
 * Rückwärts = „was self für other ist": bei src-Zeile = R, bei dest-Zeile = L (rawRelType).
 *
 * `revValue` belegt zusätzlich den Gegenrichtungs-Select bei Katalog-Labels vor —
 * aber nur, wenn der gespeicherte Wert dort überhaupt wählbar ist. Passt er nicht
 * (Alt-Zeile ohne gespeicherte Gegenrichtung, oder das inzwischen aus dem Katalog
 * entfernte „Verwandt"), bleibt das Feld leer und die Pflichtauswahl greift.
 */
function bezPrefill(e: EditRelation, selfId?: number): { selectValue: string; freeFwd: string; freeRev: string; revValue: string } {
  const isSrc = selfId != null && e.srcId === selfId;
  const fwd = (e.relType ?? "").trim();
  const rev = ((isSrc ? e.reverseRelType : e.rawRelType) ?? "").toString().trim();
  if (isCatalogLabel(fwd)) {
    return { selectValue: fwd, freeFwd: "", freeRev: "", revValue: reverseOptions(fwd).includes(rev) ? rev : "" };
  }
  return { selectValue: RELATIONSHIP_FREE, freeFwd: fwd, freeRev: rev, revValue: "" };
}

/**
 * Live-Vorschau-Text für eine Firma↔Firma-Verbindung (kanonische Wirkung).
 * C = aktueller Kontakt (`selfName`), F = gewählte Firma (`otherName`).
 */
function firmaConnPreview(role: string, selfName: string | undefined, otherName: string): string {
  const c = (selfName && selfName.trim()) || "diese Firma";
  const f = otherName || "der Firma";
  switch (role) {
    case TOCHTERFIRMA: return `${c} wird Mutterfirma von ${f}`;
    case MUTTERFIRMA:  return `${f} wird Mutterfirma von ${c}`;
    case SCHWESTER:    return `${c} und ${f} werden Schwesterfirmen`;
    // Gerichtet seit 09/2026 (Entscheidung T2): src = Gesellschaft,
    // dest = Gesellschafter. Der Satz muss deshalb sagen, WER hält.
    case BETEILIGUNG:  return `${c} hält Anteile an ${f}`;
    case GESELLSCHAFTERIN: return `${f} hält Anteile an ${c}`;
    // Fachlich symmetrisch: zwei Partner halten ein gemeinsames Unternehmen.
    // Die Schwester-Kaskade fasst diese Kante NICHT an (sie hängt allein an
    // 'Tochterfirma').
    case JOINT_VENTURE: return `${c} und ${f} werden Joint-Venture-Partner`;
    default:           return `${c} ↔ ${f}`;
  }
}

/** Gesammelte Beziehung (Collect-Modus, z.B. Kontaktanlage ohne contactId). */
export type CollectedRelation = {
  kind: RelKind;
  other: { id: number; name: string; type: string };
  relationType: string;
  reverseRelationType?: string;
  function?: string;
  share?: string;
  isExtern: boolean;
  sameHousehold: boolean;
  roleLabel: string;
};

/**
 * D4 (Plan 2026-09-30): Nachname aus dem Anzeigenamen „Nachname, Vorname" —
 * nur, wenn er eindeutig ablesbar ist (mit Komma oder ein einzelnes Wort).
 */
export function nachnameAus(name?: string | null): string | undefined {
  const n = (name ?? "").trim();
  if (!n) return undefined;
  if (n.includes(",")) return n.split(",")[0].trim() || undefined;
  return n.includes(" ") ? undefined : n;
}

export function RelationCreateDialog({
  contactId,
  kind,
  selfName,
  selfGender,
  selfSalutation,
  editRelation,
  onClose,
  onSaved,
  onCollect,
  initialLabel,
}: {
  /** Detail-Modus: Ausgangskontakt-id (sofortiges POST). Im Collect-Modus weglassen. */
  contactId?: number;
  kind: RelKind;
  /** Name des aktuellen Kontakts — für die Richtungs-Vorschau („… von <Name>"). */
  selfName?: string;
  /** Geschlecht des aktuellen Kontakts — bestimmt die Gegenrichtung („was ist
   *  self für other?"). Fehlt es, wird sie NICHT geraten, sondern erfragt. */
  selfGender?: string | null;
  /** Anrede des aktuellen Kontakts („Herr"/„Frau") — Zweitquelle für das
   *  Geschlecht, wenn `gender` nicht gepflegt ist (genderOf wertet beides aus).
   *  Ohne diesen Prop wäre der Anrede-Zweig von genderOf hier tot, weil der
   *  Dialog nur ein synthetisches `{ gender }`-Objekt übergibt. */
  selfSalutation?: string | null;
  /** Edit-Modus: bestehende Zeile vorbefüllen + Gegen-Kontakt fixieren (PATCH statt POST). */
  editRelation?: EditRelation;
  onClose: () => void;
  /** Detail-Modus: nach erfolgreichem POST/PATCH. */
  onSaved?: () => void;
  /** Collect-Modus (Anlage): liefert die Spezifikation statt zu POSTen. */
  onCollect?: (spec: CollectedRelation) => void;
  /**
   * Haushalt/Weitere, nur beim Anlegen: vorbelegte Beziehung statt des ersten
   * Katalog-Eintrags — etwa „Ehefrau" aus dem Popup „Ehepartner erfassen"
   * (T3). Muss ein Katalog-Label sein, sonst greift der Standard.
   */
  initialLabel?: string;
}) {
  // Edit-Modus: alle Eingabefelder aus der bestehenden Zeile vorbelegen.
  const isEdit = !!editRelation;
  // Prefill für household/weitere: Vorwärts (= angezeigtes Label) + Rückwärts.
  const bezInit = editRelation && (kind === "household" || kind === "weitere")
    ? bezPrefill(editRelation, contactId)
    : null;
  const [relLabel, setRelLabel] = React.useState<string>(
    bezInit?.selectValue ?? (initialLabel && isCatalogLabel(initialLabel) ? initialLabel : RELATIONSHIP_CATALOG[0]),
  );
  const [freeFwd, setFreeFwd] = React.useState<string>(bezInit?.freeFwd ?? "");
  const [freeRev, setFreeRev] = React.useState<string>(bezInit?.freeRev ?? "");
  // Manuell gewählte Gegenrichtung — nur relevant, wenn selfs Geschlecht fehlt.
  const [revLabel, setRevLabel] = React.useState<string>(bezInit?.revValue ?? "");
  const [other, setOther] = React.useState<any | null>(
    editRelation
      ? { id: editRelation.other.id, displayName: editRelation.other.name, type: { type: editRelation.other.type }, gender: editRelation.other.gender }
      : null,
  );
  const [role, setRole] = React.useState(
    editRelation && kind === "personFirma" ? personFirmaRoleFromEdit(editRelation) : kind === "personFirma" ? PERSON_FIRMA_ROLES[0] : GFG_ROLES[0],
  );
  const [verant, setVerant] = React.useState<string>(
    editRelation?.function && (kind === "verant" || kind === "personFirma") ? editRelation.function : VERANT_FUNCTIONS[0]?.value ?? "",
  );
  const [isExtern, setIsExtern] = React.useState(editRelation?.isExtern ?? false);
  const [share, setShare] = React.useState(editRelation?.share ?? "");
  // Vorbelegung aus dem GESPEICHERTEN Typ plus der Richtung der Zeile — nicht
  // aus dem Anzeige-Label. „Beteiligung" ist seit 09/2026 gerichtet: Auf der
  // Akte der Gesellschaft (src) heißt dieselbe Zeile „Gesellschafterin". Wer
  // hier das Label übernähme, drehte die Kante beim bloßen Öffnen und
  // Speichern um.
  const [firmaRole, setFirmaRole] = React.useState(
    editRelation && kind === "firmaConn"
      ? firmaConnRolleAusRichtung(
          editRelation.rawRelType ?? editRelation.relType,
          editRelation.srcId === contactId,
        )
      : FIRMA_CONN_ROLE_VALUES[0],
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    alleFenster.addEventListener("keydown", onKey);
    return () => alleFenster.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isBeziehung = kind === "household" || kind === "weitere";
  const showShare =
    (kind === "gfg" && role !== "Geschäftsführer") ||
    (kind === "personFirma" && (role === "Gesellschafter" || role === "Geschäftsführer und Gesellschafter")) ||
    kind === "firmaConn";
  const showVerant = kind === "verant" || (kind === "personFirma" && role === "Verantwortlichkeit");

  const isFree = isBeziehung && relLabel === RELATIONSHIP_FREE;
  const selfG = genderOf({ gender: selfGender, salutationLabel: selfSalutation });
  // Vorwärts = „was other für self ist"; Rückwärts = „was self für other ist".
  const bezForward = isFree ? freeFwd.trim() : relLabel;

  // Gegenrichtung: Sie beschreibt SELF und hängt damit an SELFS Geschlecht. Ist das
  // hinterlegt, ist sie eindeutig ableitbar (kein zusätzliches Feld, alles wie
  // bisher). Fehlt es, ist sie bei JEDEM Katalog-Label mehrdeutig — reverseKinship-
  // Label fiel dann auf Sammel-Antworten wie „Verwandt" zurück (der gemeldete
  // Cousin-Fall). Statt zu raten, lassen wir wählen.
  const revOptions = React.useMemo(
    () => (isBeziehung && !isFree && isCatalogLabel(relLabel) ? reverseOptions(relLabel) : []),
    [isBeziehung, isFree, relLabel],
  );
  const needsRevChoice = selfG === null && revOptions.length > 0;
  // Bewusst aus den aktuellen Optionen abgeleitet statt im State nachgeführt: nach
  // einem Wechsel des Vorwärts-Labels passt die alte Wahl nicht mehr und fällt so
  // von selbst auf „nicht gewählt" zurück (die Pflichtauswahl greift dann erneut).
  const revValue = revOptions.includes(revLabel) ? revLabel : "";

  // Die WIRKSAME Gegenrichtung — für Vorschau und für die Richtung beim Bearbeiten.
  const bezReverse = isFree
    ? freeRev.trim() || undefined
    : selfG === null
      ? revValue || undefined
      : catalogReverseLabel(relLabel, selfG);
  const previewRev = bezReverse ?? "";

  // Was DAUERHAFT in `reverseRelationType` landet: nur, was sich nicht aus dem
  // Geschlecht ableiten lässt — also Freitext oder die eben getroffene Wahl.
  // Ist selfs Geschlecht bekannt, bleibt die Spalte bewusst leer und die Anzeige
  // rechnet bei jedem Rendern neu. Das ist kein Detail: würde man den abgeleiteten
  // Wert mitspeichern, bliebe er stehen, wenn jemand später das Geschlecht
  // korrigiert — aus „Sohn/Vater" würde nach der Korrektur auf weiblich nicht
  // „Sohn/Mutter", sondern es bliebe für immer „Vater". Bei knapp 3.900 Kontakten
  // ohne hinterlegtes Geschlecht ist genau dieses Nachpflegen der Normalfall.
  const storedReverse = isFree || selfG === null ? bezReverse : undefined;

  // Kind-spezifische Felder (ohne Richtung/src-dest).
  function core(): { relationType: string; func?: string; share?: string; isExtern: boolean; sameHousehold: boolean; reverseRelationType?: string } | null {
    let relationType = "";
    let func: string | undefined;
    let shareVal: string | undefined;
    let isExt = false;
    let sameHousehold = false;
    const applyGfg = (r: string) => {
      relationType = r === "Geschäftsführer und Gesellschafter" ? "Gesellschafter" : r;
      if (r === "Geschäftsführer und Gesellschafter") func = "geschäftsführend";
    };
    if (kind === "gfg") {
      applyGfg(role);
      if (role !== "Geschäftsführer") shareVal = share.trim() || undefined;
    } else if (kind === "verant") {
      relationType = "Mensch fürs Unternehmen";
      func = verant;
      isExt = isExtern;
    } else if (kind === "firmaConn") {
      relationType = firmaRole;
      shareVal = share.trim() || undefined;
    } else if (kind === "personFirma") {
      if (role === "Verantwortlichkeit") {
        relationType = "Mensch fürs Unternehmen";
        func = verant;
        isExt = isExtern;
      } else {
        applyGfg(role);
        if (role !== "Geschäftsführer") shareVal = share.trim() || undefined;
      }
    } else if (kind === "household") {
      relationType = bezForward;
      sameHousehold = true;
    } else if (kind === "weitere") {
      relationType = bezForward;
      sameHousehold = false;
    }
    if (!relationType) return null;
    // Gespeichert wird die Gegenrichtung nur, wenn sie nicht ableitbar ist
    // (siehe `storedReverse`) — sonst fröre der abgeleitete Wert ein.
    return { relationType, func, share: shareVal, isExtern: isExt, sameHousehold, reverseRelationType: isBeziehung ? storedReverse : undefined };
  }

  // Anzeige-Rolle für die Sammel-Tabelle.
  function roleLabel(c: { func?: string; relationType: string }): string {
    if (kind === "verant" || (kind === "personFirma" && role === "Verantwortlichkeit")) return c.func || "Verantwortlichkeit";
    if (kind === "gfg" || kind === "personFirma") return role;
    if (kind === "firmaConn") return firmaRole;
    return c.relationType;
  }

  function otherName(): string {
    return other?.displayName || `${other?.lastname ?? ""}${other?.firstName ? ", " + other.firstName : ""}`.replace(/^,\s*|,\s*$/g, "").trim() || "—";
  }

  // Edit-Modus speichern. Ziel-Richtung + relationType der Zeile werden kanonisch
  // bestimmt (firmaConn via canonicalFirmaConn). Entspricht die Richtung der
  // bestehenden Zeile → in-place PATCH; KIPPT sie nur beim echten FIRMA-Richtungs-
  // wechsel (z. B. Tochter↔Mutter) → alte Zeile(n) zwischen den Kontakten löschen und
  // kanonisch neu anlegen (das Backend erzeugt daraus die Schwester-Kaskade; die
  // implizit erzeugten Schwester-Kanten bereinigt das Backend bei Degradierung
  // selbst). household/weitere (Person↔Person) werden IMMER in-place aktualisiert.
  async function saveEdit(
    c: { relationType: string; func?: string; share?: string; isExtern: boolean; sameHousehold: boolean },
    otherId: number,
    e: EditRelation,
  ) {
    const selfId = contactId as number;
    let dSrc: number;
    let dDest: number;
    // `null` = relationType NICHT mit-patchen (DB-Wert unverändert lassen).
    let dType: string | null;
    // reverseRelationType-Patch: undefined = nicht patchen, null = leeren, string = setzen.
    let dReverse: string | null | undefined = undefined;
    if (kind === "firmaConn") {
      const canon = canonicalFirmaConn(firmaRole, selfId, otherId);
      dSrc = canon.contactSrcId;
      dDest = canon.contactDestId;
      dType = canon.relationType;
    } else if (kind === "personFirma") {
      dSrc = otherId;
      dDest = selfId;
      dType = c.relationType;
    } else {
      // household / weitere: in-place (Richtung S→D bleibt). Vorwärts = „was other
      // für self ist", Rückwärts = „was self für other ist". L = relationType (was
      // D für S ist), R = reverseRelationType (was S für D ist).
      //
      // Beide Zweige sind symmetrisch: welches der beiden Labels in welche Spalte
      // geht, entscheidet allein die Richtung der gespeicherten Zeile. Früher
      // wurde auf dest-Zeilen die Gegenrichtung aus dem Geschlecht NEU abgeleitet
      // und `reverseRelationType` dabei geleert — damit wäre eine manuell gewählte
      // Gegenrichtung schon beim ersten Bearbeiten wieder verschwunden.
      dSrc = e.srcId;
      dDest = e.destId;
      const isSrcRow = e.srcId === selfId;
      const unchanged =
        !!bezInit &&
        relLabel === bezInit.selectValue &&
        freeFwd.trim() === bezInit.freeFwd &&
        freeRev.trim() === bezInit.freeRev &&
        // Ohne diesen Vergleich gälte eine reine Gegenrichtungs-Änderung als
        // „nichts geändert" und würde stillschweigend verworfen.
        revValue === (bezInit.revValue ?? "");
      if (unchanged) {
        dType = null;               // relationType nicht patchen
        dReverse = undefined;       // reverseRelationType nicht patchen
      } else if (isSrcRow) {
        // src-Zeile: relationType = „was other für self ist" (das gewählte Label),
        // reverseRelationType = „was self für other ist". Letzteres wird nur
        // festgeschrieben, wenn es nicht ableitbar ist — `null` leert die Spalte
        // wieder, falls dort ein alter, inzwischen ableitbarer Wert steht.
        dType = bezForward;
        dReverse = storedReverse ?? null;
      } else {
        // dest-Zeile: die Spalten sind vertauscht. relationType ist hier „was self
        // für other ist" — also die WIRKSAME Gegenrichtung, die immer gesetzt sein
        // muss; reverseRelationType trägt das ausdrücklich gewählte Vorwärts-Label.
        dType = bezReverse ?? bezForward;
        dReverse = bezForward;
      }
    }
    const sameDir = dSrc === e.srcId && dDest === e.destId;
    const reverseDir = dSrc === e.destId && dDest === e.srcId;
    // Symmetrische Firma-Typen sind richtungsneutral → reverse zählt als „passend".
    const inPlace = sameDir || (FIRMA_SYMMETRIC.has(dType ?? "") && reverseDir);

    setSaving(true);
    setError("");
    try {
      if (inPlace) {
        await sendSave(apiUrl("contact-relation/" + e.relationId), {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // dType === null → relationType weglassen (DB-Wert bleibt unverändert).
            ...(dType != null ? { relationType: dType } : {}),
            ...(dReverse !== undefined ? { reverseRelationType: dReverse } : {}),
            // `function` nur mitschicken, wenn diese Dialog-Art sie überhaupt
            // pflegt: core() liefert für 'firmaConn' kein `func`, der leere String
            // löschte bisher eine am Bestand hängende Zuständigkeit derselben
            // Zeile mit — eine als „Verbindung zu Firmen" einsortierte
            // Steuerkanzlei verlor beim blossen Öffnen und Speichern ihr „Steuern".
            ...(kind === "firmaConn" ? {} : { function: c.func ?? "" }),
            share: c.share ?? "",
            isExtern: c.isExtern,
            sameHousehold: c.sameHousehold,
          }),
        });
      } else {
        await sendSave(apiUrl(`contact-relation/between/${selfId}/${otherId}`), { method: "DELETE" });
        await sendSave(apiUrl("contact-relation"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contactSrcId: dSrc,
            contactDestId: dDest,
            relationType: dType,
            ...(c.func ? { function: c.func } : {}),
            ...(c.share ? { share: c.share } : {}),
            isExtern: c.isExtern,
            sameHousehold: c.sameHousehold,
          }),
        });
      }
    } catch (err) {
      setError(saveErrorText(err));
      return;
    } finally {
      setSaving(false);
    }
    // Bewusst NACH dem try: ein Throw aus onSaved/onClose ist kein Speicherfehler
    // und darf keine Fehlermeldung über einen erfolgreichen Vorgang schreiben.
    onSaved?.();
    onClose();
  }

  async function save() {
    const otherId = other?.id;
    if (!otherId) { setError("Bitte einen Kontakt wählen."); return; }
    const c = core();
    if (!c) { setError(isBeziehung ? "Bitte Beziehung angeben." : "Bitte Angaben vervollständigen."); return; }
    // Pflichtauswahl der Gegenrichtung: bewusst KEINE Vorbelegung, sonst wäre die
    // Rate-Antwort nur an eine andere Stelle verschoben.
    if (needsRevChoice && !revValue) {
      setError(`Bitte die Gegenrichtung angeben — für ${selfName || "diesen Kontakt"} ist kein Geschlecht hinterlegt.`);
      return;
    }
    // Freitext: hier ist gar nichts berechenbar, die Gegenrichtung kann nur aus
    // der Eingabe kommen. Dieselbe Lücke wie oben, deshalb dieselbe Pflicht.
    if (isFree && !freeRev.trim()) {
      setError(`Bitte angeben, wie ${selfName || "dieser Kontakt"} für ${otherName()} ist.`);
      return;
    }

    // Collect-Modus (Anlage): nur sammeln, kein POST.
    if (onCollect) {
      onCollect({
        kind,
        other: { id: otherId, name: otherName(), type: other.type?.type || other.typ || "" },
        relationType: c.relationType,
        reverseRelationType: c.reverseRelationType,
        function: c.func,
        share: c.share,
        isExtern: c.isExtern,
        sameHousehold: c.sameHousehold,
        roleLabel: roleLabel(c),
      });
      onClose();
      return;
    }

    // Edit-Modus: bestehende Zeile aktualisieren (PATCH bzw. Richtungs-Kippen).
    if (editRelation) {
      if (contactId == null) { setError("Kein Ausgangskontakt."); return; }
      await saveEdit(c, otherId, editRelation);
      return;
    }

    // Detail-Modus: direkt POSTen. Richtung + relationType bestimmen:
    //   personFirma → Firma=src (Person=dest).
    //   firmaConn   → Rolle→KANONISCH (Mutterfirma flippt Richtung + wird 'Tochterfirma';
    //                 das Backend erzeugt daraus automatisch die Schwester-Kanten).
    //   sonst       → aktueller Kontakt=src.
    if (contactId == null) { setError("Kein Ausgangskontakt."); return; }
    let src: number;
    let dest: number;
    let relationType = c.relationType;
    if (kind === "firmaConn") {
      const canon = canonicalFirmaConn(firmaRole, contactId, otherId);
      src = canon.contactSrcId;
      dest = canon.contactDestId;
      relationType = canon.relationType;
    } else if (kind === "personFirma") {
      src = otherId;
      dest = contactId;
    } else {
      src = contactId;
      dest = otherId;
    }
    const payload = {
      contactSrcId: src,
      contactDestId: dest,
      relationType,
      ...(c.reverseRelationType ? { reverseRelationType: c.reverseRelationType } : {}),
      ...(c.func ? { function: c.func } : {}),
      ...(c.share ? { share: c.share } : {}),
      isExtern: c.isExtern,
      sameHousehold: c.sameHousehold,
    };
    setSaving(true);
    setError("");
    try {
      await sendSave(apiUrl("contact-relation"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      setError(saveErrorText(err));
      return;
    } finally {
      setSaving(false);
    }
    // A2 (Plan 2026-09-30, Entscheidung 30.09.2026): Ein BESTANDSKONTAKT
    // kommt in den Haushalt und übernimmt die Anschrift des Hauptkontakts
    // automatisch — seine bisherige bleibt als weitere Anschrift.
    // T-283 (Dirk 10.10.2026): Hat der Hauptkontakt KEINE Anschrift, das
    // Mitglied aber schon, übernimmt umgekehrt der Hauptkontakt dessen
    // Standardanschrift. Das gilt auch für einen hier frisch angelegten (er
    // kann aus der Vollanlage eine mitbringen); hat der Hauptkontakt eine, hat
    // der Server sie ihm beim Anlegen schon gegeben und ändert nichts.
    if (kind === "household" && c.sameHousehold) {
      if (!(await haushaltsanschriftAbgleichen(otherId, contactId))) {
        await hinweisen({
          titel: "Anschrift nicht übernommen",
          text: `Die Beziehung ist gespeichert, die gemeinsame Anschrift des Haushalts mit ${otherName()} konnte aber nicht übernommen werden.`,
        });
      }
    }
    // Siehe saveEdit: Erfolgsmeldung und Fehlermeldung dürfen sich nicht kreuzen.
    onSaved?.();
    onClose();
  }

  const roleOptions = kind === "personFirma" ? PERSON_FIRMA_ROLES : GFG_ROLES;

  return createPortal(
    <div className="kvp-rd kvp-rd-scrim" onClick={onClose}>
      <div className="dm" role="dialog" aria-modal="true" style={{ width: "min(520px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">Verknüpfung</div>
              <h2>{isEdit ? EDIT_TITLES[kind] : TITLES[kind]}</h2>
            </div>
            <button className="dm-x" type="button" onClick={onClose} aria-label="Schließen">
              <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round"><path d="M5 5l10 10M15 5L5 15" /></svg>
            </button>
          </div>
        </div>
        <div className="dm-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="fld">
            <label>{isBeziehung ? "Person" : "Kontakt"}</label>
            {isEdit ? (
              // Im Edit-Modus ist der Gegen-Kontakt fix (kein Picker, gesperrte Anzeige).
              <div className="inp" style={{ display: "flex", alignItems: "center", background: "var(--surface-2, #f6f2f5)", color: "var(--ink-2)", cursor: "not-allowed" }}>
                {otherName()}
              </div>
            ) : (
              <RelationContactPicker
                defaultType={kind === "firmaConn" || kind === "personFirma" ? "Firma" : "Person"}
                personOnly={isBeziehung}
                selected={other}
                onSelect={setOther}
                // T-6 + Plan 2026-09-30 (C2–C4): Ein hier neu angelegter Kontakt
                // erbt FO/BO des Kontakts, aus dessen Akte er entsteht — aus der
                // Firma heraus wie aus der Person (Haushalt, Beziehungen,
                // Verbindung zu Unternehmen). Persönliche Kontakte vererben nie
                // (prüft der Server).
                verantwortungVonKontaktId={contactId}
                // A1/D1: Im Haushalt angelegt → Anschrift und Status vom Server.
                imHaushaltVon={kind === "household" ? contactId : undefined}
                // D4: Nachname des Hauptkontakts vorbelegen.
                nachnameVorschlag={kind === "household" ? nachnameAus(selfName) : undefined}
              />
            )}
          </div>

          {(kind === "gfg" || kind === "personFirma") && (
            <div className="fld">
              <label>Rolle</label>
              <select className="sel" value={role} onChange={(e) => setRole(e.target.value)}>
                {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
          {kind === "firmaConn" && (
            <div className="fld">
              <label>Rolle</label>
              <select className="sel" value={firmaRole} onChange={(e) => setFirmaRole(e.target.value)}>
                {FIRMA_CONN_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              {/* Live-Vorschau der kanonischen Wirkung (inkl. Auto-Schwester-Hinweis). */}
              {other ? (
                <span style={{ font: "500 12.5px/1.4 var(--ui)", color: "var(--ink-3)", marginTop: 6 }}>
                  Ergibt: <strong style={{ color: "var(--ink)" }}>{firmaConnPreview(firmaRole, selfName, otherName())}</strong>
                  {(firmaRole === TOCHTERFIRMA || firmaRole === MUTTERFIRMA)
                    ? " · Weitere Töchter werden automatisch zu Schwesterfirmen."
                    : ""}
                </span>
              ) : (
                <span style={{ font: "500 12.5px/1.4 var(--ui)", color: "var(--ink-3)", marginTop: 6 }}>
                  Erst eine Firma wählen — die Wirkung der Verbindung wird hier angezeigt.
                </span>
              )}

            </div>
          )}
          {showVerant && (
            <div className="fld">
              <label>Verantwortlichkeit</label>
              <select className="sel" value={verant} onChange={(e) => setVerant(e.target.value)}>
                {VERANT_FUNCTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
          {(kind === "verant" || (kind === "personFirma" && role === "Verantwortlichkeit")) && (
            <div className="fld">
              <label>Intern / Extern</label>
              <select className="sel" value={isExtern ? "extern" : "intern"} onChange={(e) => setIsExtern(e.target.value === "extern")}>
                <option value="intern">Intern</option>
                <option value="extern">Extern</option>
              </select>
            </div>
          )}
          {showShare && (
            <div className="fld">
              <label>Anteile</label>
              {/* Hier geht es nur noch um Struktur-Kanten des Verbunds und um
                  Person↔Firma-Beteiligungen — beides Prozent. Beim Verlassen wird
                  die Einheit angehängt, damit derselbe Wert entsteht wie im
                  Verantwortlichkeits-Popup (utils/anteilFormat); Freitext wie
                  „ca. 25" bleibt zeichengleich stehen. */}
              <input
                className="inp"
                value={share}
                onChange={(e) => setShare(e.target.value)}
                onBlur={() => setShare(formatiereAnteil(share, "PROZENT"))}
                maxLength={ANTEIL_MAX}
                placeholder="z. B. 25 %"
              />
            </div>
          )}
          {isBeziehung && (
            <div className="fld">
              <label>Beziehung</label>
              <select className="sel" value={relLabel} onChange={(e) => setRelLabel(e.target.value)}>
                {RELATIONSHIP_CATALOG.map((r) => <option key={r} value={r}>{r}</option>)}
                <option value={RELATIONSHIP_FREE}>Sonstige (Freitext) …</option>
              </select>
              {isFree && (
                <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                  <input
                    className="inp"
                    value={freeFwd}
                    onChange={(e) => setFreeFwd(e.target.value)}
                    placeholder={`Wie ist ${other ? otherName() : "die Person"} für ${selfName || "diesen Kontakt"}? (z. B. Patentante)`}
                  />
                  <input
                    className="inp"
                    value={freeRev}
                    onChange={(e) => setFreeRev(e.target.value)}
                    placeholder={`Wie ist ${selfName || "dieser Kontakt"} für ${other ? otherName() : "die Person"}? (z. B. Patenkind)`}
                  />
                </div>
              )}
              {/* Gegenrichtung nur erfragen, wenn sie NICHT eindeutig ableitbar ist
                  (selfs Geschlecht fehlt). Bei hinterlegtem Geschlecht bleibt der
                  Dialog unverändert — kein zusätzlicher Klick. */}
              {needsRevChoice && (
                <div className="fld" style={{ marginTop: 2 }}>
                  <label>Gegenrichtung</label>
                  <select className="sel" value={revValue} onChange={(e) => setRevLabel(e.target.value)}>
                    <option value="">— bitte wählen —</option>
                    {revOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <span style={{ font: "500 12.5px/1.4 var(--ui)", color: "var(--ink-3)" }}>
                    {revValue
                      ? `Wie ist ${selfName || "dieser Kontakt"} für ${other ? otherName() : "die Person"}?`
                      : `Gegenrichtung bitte wählen — das Geschlecht von ${selfName || "diesem Kontakt"} ist nicht hinterlegt.`}
                  </span>
                </div>
              )}
              {other && bezForward ? (
                <span style={{ font: "500 12.5px/1.4 var(--ui)", color: "var(--ink-3)", marginTop: 6 }}>
                  Ergibt: <strong style={{ color: "var(--ink)" }}>{bezForward}</strong>{selfName ? ` von ${selfName}` : ""}
                  {previewRev ? <> · <strong style={{ color: "var(--ink)" }}>{previewRev}</strong>{` von ${otherName()}`}</> : null}
                </span>
              ) : (
                <span style={{ font: "500 12.5px/1.4 var(--ui)", color: "var(--ink-3)", marginTop: 6 }}>
                  {needsRevChoice
                    ? "Erst eine Person wählen — die Gegenrichtung bitte zusätzlich angeben."
                    : "Erst eine Person wählen — die Gegenrichtung ergibt sich automatisch."}
                </span>
              )}
            </div>
          )}
          {error && <div style={{ color: "var(--danger)", font: "600 13.5px/1.4 var(--ui)" }}>{error}</div>}
        </div>
        <div className="dm-foot">
          <button type="button" className="rd-pop-btn" onClick={onClose}>Abbrechen</button>
          <button type="button" className="rd-pop-btn primary" onClick={save} disabled={saving}>
            {saving ? "Speichern …" : onCollect ? "Übernehmen" : "Speichern"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
